import { useCallback, useEffect, useRef } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import type { GeoItem } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useGeoItemDeepLink } from '../useGeoItemDeepLink';
import { closePothole, openPothole, potholeOpenItemStore, setProject } from './openItemStore';

/**
 * Binds `?geoItem=` to the open pothole / manhole, and centres the map on a record arrived at cold.
 * Mounted only by the Potholing tab, which owns the param — see PotholesLayer.
 *
 * `loaded` must mean the records finished loading, not that any arrived: an empty project would
 * otherwise never resolve the param, and a link into one would report nothing at all.
 *
 * `geoItems` is already filtered to the org's report type, so a link to the type this org does not
 * use resolves as not-found — the record exists but this surface never renders it.
 */
export function usePotholeDeepLink(projectId: string, geoItems: readonly GeoItem[], loaded: boolean) {
  const snapshot = useSnapshot(potholeOpenItemStore);
  const { current: map } = MapGL.useMap();

  // Read through the claim rather than mutating during render: a selection belonging to another
  // project or to the Site Data layer is neither ours to reconcile nor to write into this URL.
  const openItemId = snapshot.projectId === projectId && snapshot.surface === 'tab' ? snapshot.openItemId : null;
  useEffect(() => {
    setProject(projectId);
  }, [projectId]);

  // Only a record the URL opened gets the camera; a marker click leaves the viewport alone.
  const flyToId = useRef<string | null>(null);

  const has = useCallback((id: string) => geoItems.some((item) => item.id === id), [geoItems]);
  const onOpen = useCallback((id: string) => {
    flyToId.current = id;
    openPothole(id, 'tab');
  }, []);
  const onClose = useCallback(() => closePothole(), []);
  const onNotFound = useCallback(() => {
    Notify.error({
      title: 'Potholing',
      message: 'That record is not in this project, or you no longer have access to it.',
    });
  }, []);

  useGeoItemDeepLink({ ready: loaded, openId: openItemId, has, onOpen, onClose, onNotFound });

  // Keyed on the map instance, which is registered after this hook first runs on a cold load, and
  // on the coordinates rather than the list so a refetch does not move the camera again.
  const [lng, lat] = (openItemId && geoItems.find((item) => item.id === openItemId)?.geometry?.coordinates) || [];
  useEffect(() => {
    if (!map || !openItemId || flyToId.current !== openItemId) return;
    if (lng === undefined || lat === undefined) return;
    flyToId.current = null;
    map.flyTo({ center: { lng, lat }, duration: 1_000 });
  }, [map, openItemId, lng, lat]);
}
